import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { 
  CorporatePortfolioMovementService, 
  CorporateEnrollmentWithdrawalInfo, 
  WithdrawCorporateEnrollmentRequest,
  CorporatePortfolioMovementResult 
} from '../../../services/corporate-portfolio-movement.service';
import { CompanyService } from '../../../services/company.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-corporate-withdrawal-wizard',
  templateUrl: './corporate-withdrawal-wizard.component.html',
  styleUrls: ['./corporate-withdrawal-wizard.component.scss']
})
export class CorporateWithdrawalWizardComponent implements OnInit {
  currentStep = 1;
  totalSteps = 4;
  
  // Datos del wizard
  companies: any[] = [];
  selectedCompanyId: number | null = null;
  corporateEnrollments: CorporateEnrollmentWithdrawalInfo[] = [];
  selectedEnrollment: CorporateEnrollmentWithdrawalInfo | null = null;
  selectedInstallments: number[] = [];
  
  // Forms
  searchForm: FormGroup;
  confirmForm: FormGroup;
  
  // UI states
  isLoading = false;
  isSearching = false;
  isSubmitting = false;
  searchPerformed = false;
  
  // Resultado de la operación
  operationResult: CorporatePortfolioMovementResult | null = null;
  operationSuccess = false;
  operationError = '';
  
  // Preselección
  preselectedEnrollmentId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private corporatePortfolioService: CorporatePortfolioMovementService,
    private companyService: CompanyService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.searchForm = this.fb.group({
      searchTerm: ['', Validators.required]
    });
    
    this.confirmForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(10)]],
      notes: ['']
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['corporateEnrollmentId']) {
        this.preselectedEnrollmentId = +params['corporateEnrollmentId'];
        this.loadEnrollmentById(this.preselectedEnrollmentId);
      }
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  // Paso 1: Buscar empresa
  searchCompany(): void {
    if (this.searchForm.invalid) return;
    
    this.isSearching = true;
    this.searchPerformed = true;
    const term = this.searchForm.get('searchTerm')?.value;
    
    this.companyService.getAll().subscribe({
      next: (response) => {
        const allCompanies = this.extractData(response);
        this.companies = allCompanies.filter((c: any) => 
          c.name?.toLowerCase().includes(term.toLowerCase()) ||
          c.documentId?.includes(term)
        );
        this.isSearching = false;
      },
      error: (err) => {
        this.toastService.showToast('error', 'Error al buscar empresas');
        this.isSearching = false;
      }
    });
  }

  selectCompany(company: any): void {
    this.selectedCompanyId = company.id;
    this.loadActiveCorporateEnrollments(company.id);
  }

  loadActiveCorporateEnrollments(companyId: number): void {
    this.isLoading = true;
    this.corporatePortfolioService.getActiveCorporateEnrollmentsByCompany(companyId).subscribe({
      next: (response) => {
        this.corporateEnrollments = this.extractData(response);
        this.isLoading = false;
        if (this.corporateEnrollments.length === 0) {
          this.toastService.showToast('warning', 'Esta empresa no tiene acuerdos corporativos activos');
        }
      },
      error: (err) => {
        this.toastService.showToast('error', 'Error al cargar acuerdos corporativos');
        this.isLoading = false;
      }
    });
  }

  loadEnrollmentById(corporateEnrollmentId: number): void {
    this.isLoading = true;
    this.corporatePortfolioService.getCorporateEnrollmentWithdrawalInfo(corporateEnrollmentId).subscribe({
      next: (response) => {
        const enrollment = response.data || response;
        if (enrollment && enrollment.canBeWithdrawn) {
          this.selectedEnrollment = enrollment;
          this.selectAllInstallments();
          this.currentStep = 2;
        } else {
          this.toastService.showToast('error', 'Este acuerdo corporativo no puede ser retirado');
          this.router.navigate(['/enrollments']);
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.toastService.showToast('error', 'Error al cargar acuerdo corporativo');
        this.isLoading = false;
      }
    });
  }

  selectEnrollment(enrollment: CorporateEnrollmentWithdrawalInfo): void {
    if (!enrollment.canBeWithdrawn) {
      this.toastService.showToast('warning', 'Este acuerdo no tiene cuotas pendientes para castigar');
      return;
    }
    this.selectedEnrollment = enrollment;
    this.selectAllInstallments();
    this.currentStep = 2;
  }

  // Paso 2: Seleccionar cuotas
  toggleInstallment(paymentPlanId: number): void {
    const index = this.selectedInstallments.indexOf(paymentPlanId);
    if (index > -1) {
      this.selectedInstallments.splice(index, 1);
    } else {
      this.selectedInstallments.push(paymentPlanId);
    }
  }

  selectAllInstallments(): void {
    if (!this.selectedEnrollment) return;
    
    const companyPlans = this.selectedEnrollment.companyPendingPlans
      .filter(pp => !pp.isPunished)
      .map(pp => pp.corporatePaymentPlanId);
    
    const participantPlans = this.selectedEnrollment.participantPendingPlans
      .filter(pp => !pp.isPunished)
      .map(pp => pp.corporatePaymentPlanId);
    
    this.selectedInstallments = [...companyPlans, ...participantPlans];
  }

  deselectAllInstallments(): void {
    this.selectedInstallments = [];
  }

  isInstallmentSelected(paymentPlanId: number): boolean {
    return this.selectedInstallments.includes(paymentPlanId);
  }

  get selectedTotalAmount(): number {
    if (!this.selectedEnrollment) return 0;
    
    const companyAmount = this.selectedEnrollment.companyPendingPlans
      .filter(pp => this.selectedInstallments.includes(pp.corporatePaymentPlanId))
      .reduce((sum, pp) => sum + pp.pendingAmount, 0);
    
    const participantAmount = this.selectedEnrollment.participantPendingPlans
      .filter(pp => this.selectedInstallments.includes(pp.corporatePaymentPlanId))
      .reduce((sum, pp) => sum + pp.pendingAmount, 0);
    
    return companyAmount + participantAmount;
  }

  get selectedCompanyAmount(): number {
    if (!this.selectedEnrollment) return 0;
    return this.selectedEnrollment.companyPendingPlans
      .filter(pp => this.selectedInstallments.includes(pp.corporatePaymentPlanId))
      .reduce((sum, pp) => sum + pp.pendingAmount, 0);
  }

  get selectedParticipantAmount(): number {
    if (!this.selectedEnrollment) return 0;
    return this.selectedEnrollment.participantPendingPlans
      .filter(pp => this.selectedInstallments.includes(pp.corporatePaymentPlanId))
      .reduce((sum, pp) => sum + pp.pendingAmount, 0);
  }

  get allInstallmentsSelected(): boolean {
    if (!this.selectedEnrollment) return false;
    const total = this.selectedEnrollment.companyPendingPlans.filter(pp => !pp.isPunished).length +
                  this.selectedEnrollment.participantPendingPlans.filter(pp => !pp.isPunished).length;
    return this.selectedInstallments.length === total && total > 0;
  }

  get availableCompanyInstallmentsCount(): number {
    if (!this.selectedEnrollment) return 0;
    return this.selectedEnrollment.companyPendingPlans.filter(pp => !pp.isPunished).length;
  }

  get availableParticipantInstallmentsCount(): number {
    if (!this.selectedEnrollment) return 0;
    return this.selectedEnrollment.participantPendingPlans.filter(pp => !pp.isPunished).length;
  }

  toggleSelectAll(): void {
    if (this.allInstallmentsSelected) {
      this.deselectAllInstallments();
    } else {
      this.selectAllInstallments();
    }
  }

  // Navegación
  goToStep(step: number): void {
    if (step < this.currentStep) {
      this.currentStep = step;
    } else if (step === 2 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (step === 3 && this.selectedInstallments.length > 0) {
      this.currentStep = 3;
    }
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (this.currentStep === 2 && this.selectedInstallments.length > 0) {
      this.currentStep = 3;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  // Paso 3: Confirmar retiro
  submitWithdrawal(): void {
    if (this.confirmForm.invalid || !this.selectedEnrollment || this.isSubmitting) return;
    
    this.isSubmitting = true;
    const request: WithdrawCorporateEnrollmentRequest = {
      corporateEnrollmentId: this.selectedEnrollment.corporateEnrollmentId,
      reason: this.confirmForm.get('reason')?.value,
      corporatePaymentPlanIds: this.selectedInstallments,
      notes: this.confirmForm.get('notes')?.value
    };

    this.corporatePortfolioService.withdrawCorporateEnrollment(request).subscribe({
      next: (response) => {
        const result = response.data || response;
        this.operationResult = result;
        this.isSubmitting = false;
        
        if (result.success) {
          this.operationSuccess = true;
          this.currentStep = 4;
        } else {
          this.operationSuccess = false;
          this.operationError = result.message || 'Error al procesar retiro';
          this.currentStep = 4;
        }
      },
      error: (err) => {
        this.operationSuccess = false;
        this.operationError = 'Error al procesar retiro. Por favor, intente nuevamente.';
        this.currentStep = 4;
        this.isSubmitting = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/enrollments']);
  }

  // Paso 4: Resultado
  viewEnrollment(): void {
    if (this.selectedEnrollment) {
      this.router.navigate(['/enrollments/view-corporate', this.selectedEnrollment.corporateEnrollmentId]);
    }
  }

  goToEnrollmentsList(): void {
    this.router.navigate(['/enrollments']);
  }

  startNewWithdrawal(): void {
    this.currentStep = 1;
    this.selectedCompanyId = null;
    this.selectedEnrollment = null;
    this.selectedInstallments = [];
    this.corporateEnrollments = [];
    this.companies = [];
    this.searchPerformed = false;
    this.operationResult = null;
    this.operationSuccess = false;
    this.operationError = '';
    this.searchForm.reset();
    this.confirmForm.reset();
  }

  // Helpers para formato
  formatCurrency(amount: number, currencySymbol?: string): string {
    const symbol = currencySymbol || '$';
    return `${symbol} ${amount.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  formatDate(date: Date | string): string {
    return new Date(date).toLocaleDateString('es-CO');
  }
}

